import React, { useState, useEffect } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import subscriptionService from '../../../utils/subscriptionService';
import usageService from '../../../utils/usageService';
import stripeService from '../../../utils/stripeService';

const BillingTab = () => {
  const { currentOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (currentOrganization) {
      loadBillingData();
    }
  }, [currentOrganization]);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load subscription data
      const subscriptionResult = await subscriptionService.getSubscriptionSummary(currentOrganization.id);
      if (subscriptionResult.success) {
        setSubscriptionData(subscriptionResult.data);
      }

      // Load current usage
      const usageResult = await usageService.getCurrentMonthUsage(currentOrganization.id);
      if (usageResult.success) {
        setUsageData(usageResult.data);
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToPro = async (billingCycle = 'monthly') => {
    try {
      setActionLoading(true);
      setError(''); // Clear any previous errors
      
      const result = await stripeService.upgradeToPro(currentOrganization.id, billingCycle);
      
      if (!result.success) {
        setError(result.error || 'Failed to start upgrade process');
        return;
      }

      // Explicitly handle redirect if the service didn't do it
      if (result.data && result.data.url) {
        console.log('Redirecting to Stripe checkout:', result.data.url);
        window.location.href = result.data.url;
      } else {
        console.error('No checkout URL received:', result);
        setError('Failed to get checkout URL from Stripe');
      }
    } catch (error) {
      console.error('Error upgrading to Pro:', error);
      setError('Failed to start upgrade process');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDowngradeToFree = async () => {
    try {
      setActionLoading(true);
      const result = await stripeService.downgradeToFree(currentOrganization.id);
      
      if (result.success) {
        await loadBillingData(); // Refresh data
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Error downgrading to free:', error);
      setError('Failed to downgrade to free plan');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      const result = await stripeService.openCustomerPortal(currentOrganization.id);
      
      if (!result.success) {
        setError(result.error);
      }
      // If successful, user will be redirected to Stripe portal
    } catch (error) {
      console.error('Error opening customer portal:', error);
      setError('Failed to open subscription management');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-card rounded-lg border border-border p-8">
          <div className="flex items-center justify-center">
            <Icon name="Loader2" size={24} className="animate-spin text-text-muted" />
            <span className="ml-2 text-muted-foreground">Loading billing information...</span>
          </div>
        </div>
      </div>
    );
  }

  const isFreePlan = subscriptionData?.plan?.isFreePlan ?? true;
  const isProPlan = subscriptionData?.plan?.isProPlan ?? false;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={20} className="text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Current Plan</h3>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              isProPlan ? 'bg-primary/10' : 'bg-green-100'
            }`}>
              <Icon 
                name={isProPlan ? 'Zap' : 'Heart'} 
                size={24} 
                className={isProPlan ? 'text-primary' : 'text-green-600'} 
              />
            </div>
            <div>
              <h4 className="font-semibold text-foreground">
                {subscriptionData?.plan?.name || 'Free Plan'}
              </h4>
              <p className="text-muted-foreground">
                ${subscriptionData?.plan?.monthlyPrice || 0}/month
              </p>
            </div>
          </div>
          
          <div className="text-right">
            {isProPlan ? (
              <Button
                variant="outline"
                onClick={handleManageSubscription}
                disabled={actionLoading}
                className="flex items-center space-x-2"
              >
                <Icon name="Settings" size={16} />
                <span>Manage</span>
              </Button>
            ) : (
              <div className="space-x-2">
                <Button
                  onClick={() => handleUpgradeToPro('monthly')}
                  disabled={actionLoading}
                  className="flex items-center space-x-2"
                >
                  <Icon name="ArrowUp" size={16} />
                  <span>Upgrade to Pro</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Usage */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">AI Review Usage</h3>
        
        {usageData ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-muted-foreground">This month:</span>
              <span className="font-semibold text-foreground">
                {usageData.isUnlimited 
                  ? `${usageData.used} reviews used (Unlimited)` 
                  : `${usageData.used}/${usageData.limit} reviews used`
                }
              </span>
            </div>
            
            {!usageData.isUnlimited && (
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div 
                  className={`h-2 rounded-full transition-all ${
                    usageData.used >= usageData.limit 
                      ? 'bg-red-500' 
                      : usageData.used >= usageData.limit * 0.8 
                        ? 'bg-yellow-500' 
                        : 'bg-green-500'
                  }`}
                  style={{ 
                    width: `${Math.min((usageData.used / usageData.limit) * 100, 100)}%` 
                  }}
                />
              </div>
            )}
            
            {!usageData.canGenerate && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-red-600" />
                  <span className="text-red-800 text-sm">
                    Monthly limit reached. Upgrade to Pro for unlimited AI reviews.
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-text-muted">No usage data available</p>
        )}
      </div>

      {/* Plan Comparison */}
      {isFreePlan && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Upgrade to Pro</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Monthly Pro */}
            <div className="border border-border rounded-lg p-4">
              <div className="text-center mb-4">
                <h4 className="font-semibold text-foreground">Pro Monthly</h4>
                <div className="text-2xl font-bold text-foreground">$97</div>
                <div className="text-muted-foreground">per month</div>
              </div>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center text-sm">
                  <Icon name="Check" size={16} className="text-green-500 mr-2" />
                  Unlimited AI reviews
                </li>
                <li className="flex items-center text-sm">
                  <Icon name="Check" size={16} className="text-green-500 mr-2" />
                  Priority support
                </li>
                <li className="flex items-center text-sm">
                  <Icon name="Check" size={16} className="text-green-500 mr-2" />
                  Custom integrations
                </li>
              </ul>
              <Button 
                onClick={() => handleUpgradeToPro('monthly')}
                disabled={actionLoading}
                className="w-full"
              >
                Upgrade Monthly
              </Button>
            </div>

            {/* Yearly Pro */}
            <div className="border border-primary rounded-lg p-4 relative">
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary text-white px-2 py-1 rounded-full text-xs">
                  Save $567
                </span>
              </div>
              <div className="text-center mb-4">
                <h4 className="font-semibold text-foreground">Pro Yearly</h4>
                <div className="text-2xl font-bold text-foreground">$597</div>
                <div className="text-muted-foreground">per year</div>
              </div>
              <ul className="space-y-2 mb-4">
                <li className="flex items-center text-sm">
                  <Icon name="Check" size={16} className="text-green-500 mr-2" />
                  Unlimited AI reviews
                </li>
                <li className="flex items-center text-sm">
                  <Icon name="Check" size={16} className="text-green-500 mr-2" />
                  Priority support
                </li>
                <li className="flex items-center text-sm">
                  <Icon name="Check" size={16} className="text-green-500 mr-2" />
                  Custom integrations
                </li>
              </ul>
              <Button 
                onClick={() => handleUpgradeToPro('yearly')}
                disabled={actionLoading}
                className="w-full"
              >
                Upgrade Yearly
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade Option for Pro Users */}
      {isProPlan && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Manage Subscription</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-foreground mb-2">Need to downgrade?</h4>
              <p className="text-muted-foreground text-sm mb-3">
                You can switch to the Free plan anytime. You'll keep access to Pro features until the end of your billing period.
              </p>
              <Button
                variant="outline"
                onClick={handleDowngradeToFree}
                disabled={actionLoading}
                className="flex items-center space-x-2"
              >
                <Icon name="ArrowDown" size={16} />
                <span>Switch to Free Plan</span>
              </Button>
            </div>
            
            <div className="border-t border-border pt-4">
              <h4 className="font-medium text-foreground mb-2">Payment & Billing</h4>
              <p className="text-muted-foreground text-sm mb-3">
                Manage your payment methods, view billing history, and update subscription details through Stripe.
              </p>
              <Button
                variant="outline"
                onClick={handleManageSubscription}
                disabled={actionLoading}
                className="flex items-center space-x-2"
              >
                <Icon name="ExternalLink" size={16} />
                <span>Open Billing Portal</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingTab; 